import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

export interface DualEnvSecretEntry {
  key: string;
  stagingValue: string;
  productionValue: string;
}

const KEY_PATTERN = /^[A-Z0-9_]+$/;

/**
 * Reference count of mounted DualEnvSecretsInput instances.
 */
let _mountCount = 0;
export const isDualEnvSecretsInputMounted = () => _mountCount > 0;

/**
 * Validator for DualEnvSecretsInput field.
 * At least one secret with both staging and production values is required.
 */
export const validateDualEnvSecretsInput = (
  value: DualEnvSecretEntry[],
): void => {
  if (!isDualEnvSecretsInputMounted()) {
    return;
  }
  if (!value || value.length === 0) {
    throw new Error(
      'At least one secret is required. Add a Key with Staging and Production values above.',
    );
  }
  // Filter out entries with empty keys (shouldn't exist but guard against empty form submission)
  const validEntries = value.filter(entry => entry.key?.trim());
  if (validEntries.length === 0) {
    throw new Error(
      'At least one secret is required. Add a Key with Staging and Production values above.',
    );
  }
  // On first deploy, both values are required
  // On redeploy, blank values mean "keep existing"
};

/**
 * DualEnvSecretsInput — key with separate staging and production values.
 *
 * On initial deploy: user enters keys + values for BOTH environments.
 *
 * On redeploy: formData is pre-filled with existing keys + empty values.
 *   Each existing secret defaults to "Keeping existing" mode for both envs.
 *   User can independently update staging or production values.
 *
 *   - Keep mode (default) → value stays '' → backend skips it → GCP secret unchanged.
 *   - Change mode (user opted in) → user types new value → backend updates that env's secret.
 */
export const DualEnvSecretsInput = ({
  onChange,
  rawErrors,
  formData,
  schema,
}: FieldExtensionComponentProps<DualEnvSecretEntry[]>) => {
  // Normalize incoming formData: ensure each entry has key, stagingValue, productionValue
  // RJSF may pass entries with missing properties (e.g., from default array widget)
  const [secrets, setSecrets] = useState<DualEnvSecretEntry[]>(() => {
    const raw = formData ?? [];
    return raw
      .filter(s => !!s && typeof s === 'object')
      .map(s => ({
        key: typeof s.key === 'string' ? s.key : '',
        stagingValue: typeof s.stagingValue === 'string' ? s.stagingValue : '',
        productionValue:
          typeof s.productionValue === 'string' ? s.productionValue : '',
      }))
      .filter(s => s.key.trim() !== '');
  });
  const [newKey, setNewKey] = useState('');
  const [newStagingValue, setNewStagingValue] = useState('');
  const [newProductionValue, setNewProductionValue] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [newStagingVisible, setNewStagingVisible] = useState(false);
  const [newProductionVisible, setNewProductionVisible] = useState(false);

  // Keys that were pre-filled from redeploy (have empty values)
  // Handles both properly typed entries and RJSF-defaulted objects
  const [prefilledKeys] = useState<Set<string>>(() => {
    const raw = formData ?? [];
    const keys = raw
      .filter(s => !!s && typeof s === 'object')
      .filter(s => {
        const key = typeof s.key === 'string' ? s.key : '';
        const stagingVal =
          typeof s.stagingValue === 'string' ? s.stagingValue : '';
        const prodVal =
          typeof s.productionValue === 'string' ? s.productionValue : '';
        return key.trim() !== '' && stagingVal === '' && prodVal === '';
      })
      .map(s => s.key.trim());
    return new Set(keys);
  });

  // Track which env values are being edited (key:env format)
  const [editingEnvs, setEditingEnvs] = useState<Set<string>>(() => new Set());

  // Track visibility state for each secret key:env combo (eye icon toggle)
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(
    () => new Set(),
  );

  const isKeepMode = (key: string, env: 'staging' | 'production') =>
    prefilledKeys.has(key) && !editingEnvs.has(`${key}:${env}`);

  const enableEdit = (key: string, env: 'staging' | 'production') => {
    setEditingEnvs(prev => new Set(prev).add(`${key}:${env}`));
  };

  const toggleVisibility = (key: string, env: 'staging' | 'production') => {
    const id = `${key}:${env}`;
    setVisibleSecrets(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isVisible = (key: string, env: 'staging' | 'production') =>
    visibleSecrets.has(`${key}:${env}`);

  useEffect(() => {
    _mountCount++;
    return () => {
      _mountCount--;
    };
  }, []);

  useEffect(() => {
    onChange(secrets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (updated: DualEnvSecretEntry[]) => {
    setSecrets(updated);
    onChange(updated);
  };

  const handleAdd = () => {
    const trimmedKey = newKey.trim().toUpperCase();
    setAddError(null);

    if (!trimmedKey) {
      setAddError('Secret key is required');
      return;
    }

    if (!KEY_PATTERN.test(trimmedKey)) {
      setAddError(
        'Key must only contain uppercase letters, digits, and underscores (A-Z, 0-9, _)',
      );
      return;
    }

    if (secrets.some(s => s.key === trimmedKey)) {
      setAddError(`Key "${trimmedKey}" already exists`);
      return;
    }

    if (!newStagingValue.trim()) {
      setAddError('Staging value is required');
      return;
    }

    if (!newProductionValue.trim()) {
      setAddError('Production value is required');
      return;
    }

    update([
      ...secrets,
      {
        key: trimmedKey,
        stagingValue: newStagingValue,
        productionValue: newProductionValue,
      },
    ]);
    setNewKey('');
    setNewStagingValue('');
    setNewProductionValue('');
    setNewStagingVisible(false);
    setNewProductionVisible(false);
  };

  const handleDelete = (index: number) => {
    const removed = secrets[index];
    setEditingEnvs(prev => {
      const next = new Set(prev);
      next.delete(`${removed.key}:staging`);
      next.delete(`${removed.key}:production`);
      return next;
    });
    setVisibleSecrets(prev => {
      const next = new Set(prev);
      next.delete(`${removed.key}:staging`);
      next.delete(`${removed.key}:production`);
      return next;
    });
    update(secrets.filter((_, i) => i !== index));
  };

  const handleValueChange = (
    index: number,
    env: 'staging' | 'production',
    value: string,
  ) => {
    const updated = secrets.map((s, i) => {
      if (i !== index) return s;
      return env === 'staging'
        ? { ...s, stagingValue: value }
        : { ...s, productionValue: value };
    });
    update(updated);
  };

  const title = schema?.title || 'Application Secrets';
  const description =
    schema?.description ||
    'Enter secrets for both staging and production environments.';

  const renderValueCell = (
    secret: DualEnvSecretEntry,
    idx: number,
    env: 'staging' | 'production',
  ) => {
    const value =
      env === 'staging' ? secret.stagingValue : secret.productionValue;
    const keepMode = isKeepMode(secret.key, env);
    const envLabel = env.charAt(0).toUpperCase() + env.slice(1);
    const isPreFilled = prefilledKeys.has(secret.key);

    if (keepMode) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<LockIcon />}
            label="Keeping existing"
            size="small"
            color="success"
            variant="outlined"
          />
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => enableEdit(secret.key, env)}
            variant="text"
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Change
          </Button>
        </Box>
      );
    }

    // SECURITY: Eye icon ONLY for brand-new secrets, NEVER for pre-filled keys
    const showEyeIcon = !isPreFilled;

    return (
      <TextField
        type={showEyeIcon && isVisible(secret.key, env) ? 'text' : 'password'}
        size="small"
        placeholder={
          isPreFilled ? `New ${env} value (blank = keep)` : `${envLabel} value`
        }
        value={value}
        onChange={e => handleValueChange(idx, env, e.target.value)}
        fullWidth
        variant="outlined"
        inputProps={{
          'aria-label': `${envLabel} value for secret ${secret.key}`,
        }}
        InputProps={
          showEyeIcon
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={`Toggle visibility for ${secret.key} ${env}`}
                      aria-pressed={isVisible(secret.key, env)}
                      onClick={() => toggleVisibility(secret.key, env)}
                      onMouseDown={e => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {isVisible(secret.key, env) ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{
          '& input[type="password"]::-ms-reveal': { display: 'none' },
          '& input[type="password"]::-webkit-credentials-auto-fill-button': {
            display: 'none',
          },
        }}
      />
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">{title}</Typography>
        <Tooltip
          title="Secrets are stored separately for each environment in GCP Secret Manager.
          The STAGING_ prefix is added internally and hidden from your application."
        >
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>
      <FormHelperText sx={{ mb: 1 }}>{description}</FormHelperText>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>How it works:</strong> Each secret is stored twice in GCP
          Secret Manager &mdash; once for staging (with internal STAGING_
          prefix) and once for production. Your app always reads from{' '}
          <code>/var/secrets/&#123;KEY&#125;.txt</code> regardless of
          environment.
        </Typography>
      </Alert>

      {secrets.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>
                <Typography variant="caption" fontWeight="bold">
                  KEY
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '35%' }}>
                <Typography variant="caption" fontWeight="bold">
                  STAGING VALUE
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '35%' }}>
                <Typography variant="caption" fontWeight="bold">
                  PRODUCTION VALUE
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '10%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {secrets.map((secret, idx) => (
              <TableRow key={secret.key}>
                <TableCell>
                  <Chip label={secret.key} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{renderValueCell(secret, idx, 'staging')}</TableCell>
                <TableCell>
                  {renderValueCell(secret, idx, 'production')}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={`Remove secret ${secret.key}`}
                    onClick={() => handleDelete(idx)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add New Secret
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 2fr 2fr auto',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Key (e.g., DB_PASSWORD)"
          value={newKey}
          onChange={e => {
            setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''));
            setAddError(null);
          }}
          size="small"
          variant="outlined"
          inputProps={{ 'aria-label': 'New secret key' }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <TextField
          placeholder="Staging Value"
          type={newStagingVisible ? 'text' : 'password'}
          value={newStagingValue}
          onChange={e => {
            setNewStagingValue(e.target.value);
            setAddError(null);
          }}
          size="small"
          variant="outlined"
          inputProps={{ 'aria-label': 'New staging secret value' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle visibility for new staging secret value"
                  aria-pressed={newStagingVisible}
                  onClick={() => setNewStagingVisible(prev => !prev)}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                  size="small"
                >
                  {newStagingVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& input[type="password"]::-ms-reveal': { display: 'none' },
            '& input[type="password"]::-webkit-credentials-auto-fill-button': {
              display: 'none',
            },
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <TextField
          placeholder="Production Value"
          type={newProductionVisible ? 'text' : 'password'}
          value={newProductionValue}
          onChange={e => {
            setNewProductionValue(e.target.value);
            setAddError(null);
          }}
          size="small"
          variant="outlined"
          inputProps={{ 'aria-label': 'New production secret value' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle visibility for new production secret value"
                  aria-pressed={newProductionVisible}
                  onClick={() => setNewProductionVisible(prev => !prev)}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                  size="small"
                >
                  {newProductionVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& input[type="password"]::-ms-reveal': { display: 'none' },
            '& input[type="password"]::-webkit-credentials-auto-fill-button': {
              display: 'none',
            },
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="medium"
        >
          + ADD SECRET
        </Button>
      </Box>

      {addError && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {addError}
        </FormHelperText>
      )}

      {secrets.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          At least one secret is required. Add a Key with both Staging and
          Production values above to continue.
        </Alert>
      )}

      {(rawErrors?.length ?? 0) > 0 && secrets.length > 0 && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {rawErrors![0]}
        </FormHelperText>
      )}
    </Box>
  );
};

/**
 * Validation sentinel for dual-env secrets.
 * Same pattern as SecretsValidationField but checks for DualEnvSecretEntry structure.
 */
export const DualEnvSecretsValidationField = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const formData = formContext?.formData ?? {};
  const enableSecrets = formData.enableSecrets as boolean | undefined;
  const secretsMode = formData.secretsMode as string | undefined;
  const secrets = formData.secrets as DualEnvSecretEntry[] | undefined;

  const isInvalid =
    enableSecrets === true &&
    (secretsMode === undefined || secretsMode === 'SkyStage Managed') &&
    (!secrets || secrets.length === 0);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    onChangeRef.current(isInvalid ? 'INVALID' : 'OK');
  }, [isInvalid]);

  return <Box sx={{ display: 'none' }} />;
};
